import { Box, Stack, SxProps } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { LoadedContent } from "components/common/loading/loadedContent";
import { MultiSelect } from "components/common/inputs/multiSelect";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid";
import { SelectBox } from "components/strategyDashboard/Selectbox";
import { DateRangeSelector } from "components/common/DateRangeSelector";
import { useDateRange } from "contexts/DateRangeContext";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useMaterialDisplayName } from "hooks/useMaterials";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { useStringQueryParamState } from "hooks/useStringQueryParamState";
import { TFunction } from "i18next";
import { useEffect, useMemo, useState } from "react";
import {
  StrategyDashboardComplianceData,
  UnitType,
} from "src/store/api/generatedApi";
import theme from "src/theme";
import { ColouredCell } from "../ColouredCell";
import { ToggleSelector } from "../ToggleSelector";
import { defaultTableSx } from "../tableFormatting";
import { Fetching } from "components/common/loading/fetching";
import {
  useComplianceUnitsFormatter,
  useStrategyDashboardCompliance,
} from "./complianceHooks";
import { CompliancePodium } from "./CompliancePodium";
import { useOutlierHeats } from "./OutlierHeatsContext";
import {
  buildDiffSourceReferenceMap,
  computeComplianceDiffs,
  computeCompliancePodiumScores,
  DiffRow,
} from "./utils";
import { sorted } from "helpers";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

const ALL_CREW_ID = "All";

const DEFAULT_COMPLIANCE_UNIT: UnitType = "mass_fraction";

type HeatId = string;

const EMPTY_HEAT_IDS: HeatId[] = [];

type ComplianceValue = StrategyDashboardComplianceData["values"][number];

export const RankingComplianceDiffTable = () => {
  const { startDate, endDate } = useDateRange();
  const complianceData = useStrategyDashboardCompliance(
    startDate.format(),
    endDate.format()
  );
  const fetching =
    complianceData.status === "success" ? complianceData.fetching : false;

  return (
    <LoadedContent data={complianceData}>
      {(data) => (
        <RankingComplianceDiffTableContent
          complianceData={data}
          fetching={fetching}
        />
      )}
    </LoadedContent>
  );
};

interface RankingComplianceDiffTableContentProps {
  complianceData: StrategyDashboardComplianceData;
  fetching: boolean;
}

const RankingComplianceDiffTableContent = ({
  complianceData,
  fetching,
}: RankingComplianceDiffTableContentProps) => {
  const { t } = useTenantTranslation();
  const formatUnits = useComplianceUnitsFormatter();
  const units = useUnitsFormatter(false);
  const { format } = useNumberSerialiser({ decimalPlaces: 0 });
  const hasComplianceCost = useIsBehindFeatureFlag("compliance_cost");
  const hasCompliancePerHeat = useIsBehindFeatureFlag("compliance_per_heat");
  const { openOutlierHeats } = useOutlierHeats();
  const showToggles =
    complianceData.available_recipe_compliance_diff_sources.length > 1;

  const [selectedProductGroup, setSelectedProductGroup] =
    useStringQueryParamState<string>("productGroup", "All");

  const defaultDiffSource =
    complianceData.available_recipe_compliance_diff_sources[0] ?? "None";
  const [diffSourceParam, setSelectedDiffSource] = useStringQueryParamState<
    string | null
  >("complianceDiffSource", null);
  const selectedDiffSource =
    diffSourceParam &&
    complianceData.available_recipe_compliance_diff_sources.includes(
      diffSourceParam
    )
      ? diffSourceParam
      : defaultDiffSource;

  const [unitParam, setSelectedUnit] = useStringQueryParamState<string | null>(
    "complianceUnit",
    null
  );
  const selectedUnit: UnitType = showToggles
    ? unitParam &&
      (complianceData.available_units as string[]).includes(unitParam)
      ? (unitParam as UnitType)
      : DEFAULT_COMPLIANCE_UNIT
    : DEFAULT_COMPLIANCE_UNIT;

  const diffSourceReferenceLabels = useMemo(
    () => buildDiffSourceReferenceMap(complianceData.values),
    [complianceData.values]
  );

  const filteredValues = useMemo(
    () =>
      complianceData.values.filter(
        (v) =>
          v.product_group_name === selectedProductGroup &&
          v.recipe_compliance_diff_source === selectedDiffSource
      ),
    [complianceData.values, selectedProductGroup, selectedDiffSource]
  );

  const allMaterialNames = useMemo(
    () =>
      sorted(
        Array.from(
          new Set(
            filteredValues.flatMap((v) =>
              v.consumption.map((c) => c.material_name)
            )
          )
        ),
        (name) => name
      ),
    [filteredValues]
  );

  const [selectedMaterials, setSelectedMaterials] = useState(allMaterialNames);

  useEffect(() => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets selection when data changes; user also selects
    setSelectedMaterials(allMaterialNames);
  }, [allMaterialNames]);

  const materialFilteredValues = useMemo(() => {
    const selectedSet = new Set(selectedMaterials);
    return filteredValues.map((value) => ({
      ...value,
      consumption: value.consumption.filter((c) =>
        selectedSet.has(c.material_name)
      ),
    }));
  }, [filteredValues, selectedMaterials]);

  const crewIds = useMemo(() => {
    const named = sorted(
      complianceData.available_crews.filter((c) => c !== "All"),
      (c) => c
    );
    return complianceData.available_crews.includes("All")
      ? [...named, "All"]
      : named;
  }, [complianceData.available_crews]);

  const { rows, totalsRow, costDiffRow, maxAbsDiff } = useMemo(
    () => buildRowsAndTotals(materialFilteredValues, crewIds, t, selectedUnit),
    [materialFilteredValues, crewIds, t, selectedUnit]
  );

  const { crewScores, overallCompliancePercent } = useMemo(
    () => computeCompliancePodiumScores(materialFilteredValues, crewIds),
    [materialFilteredValues, crewIds]
  );

  const formatSpecificCost = useMemo(
    () => (value: number) =>
      `${value > 0 ? "+" : ""}${units("cost", false)}${format(value, { decimalPlaces: 2 })}/${units("mass", false)}`,
    [units, format]
  );

  const materialDisplayName = useMaterialDisplayName();

  const columns = buildColumns({
    crewIds,
    t,
    formatUnits,
    maxAbsDiff,
    formatSpecificCost,
    unit: selectedUnit,
    onAllOutlierClick: hasCompliancePerHeat ? openOutlierHeats : undefined,
    materialDisplayName,
  });

  const bottomPinnedRows =
    hasComplianceCost && costDiffRow ? [totalsRow, costDiffRow] : [totalsRow];

  return (
    <Box
      sx={{
        mt: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "stretch",
          width: "50vw",
          gap: 4,
          mt: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            flex: 1,
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            justifyContent="space-between"
            alignItems="flex-end"
          >
            <DateRangeSelector />
            {showToggles ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <ToggleSelector<string>
                  selectedValue={selectedDiffSource}
                  setSelectedValue={setSelectedDiffSource}
                  availableValueLabels={Object.fromEntries(
                    complianceData.available_recipe_compliance_diff_sources.map(
                      (diffSource) => [
                        diffSource,
                        diffSourceReferenceLabels.get(diffSource) ?? diffSource,
                      ]
                    )
                  )}
                />
                <ToggleSelector<"mass" | "mass_fraction">
                  selectedValue={selectedUnit as "mass" | "mass_fraction"}
                  setSelectedValue={setSelectedUnit}
                  availableValueLabels={{
                    mass: "mass",
                    mass_fraction: "percentage",
                  }}
                />
              </Stack>
            ) : null}
          </Stack>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 2,
              "& > .MuiFormControl-root": { flex: 1, minWidth: 160 },
            }}
          >
            <SelectBox
              id="product-group-select"
              labelId="product-group-label"
              label={t("productGroup")}
              availableOptions={complianceData.available_product_groups}
              selectedOption={selectedProductGroup}
              onOptionChange={setSelectedProductGroup}
            />
            <MultiSelect
              options={allMaterialNames}
              values={selectedMaterials}
              setValues={setSelectedMaterials}
              label={t("material")}
              getOptionLabel={(name) => name}
              getOptionId={(name) => name}
              small
              selectAll
              labelAbove
              limitTags={1}
            />
          </Box>
        </Box>
        <CompliancePodium
          crewScores={crewScores}
          overallCompliancePercent={overallCompliancePercent}
        />
      </Box>
      <Box sx={{ width: "50vw" }}>
        <Fetching fetching={fetching}>
          <HotkeyCompliantDataGrid
            sx={complianceTableSx}
            rows={rows}
            pinnedRows={{ bottom: bottomPinnedRows }}
            columns={columns}
            getRowClassName={(params) =>
              params.id === "total" || params.id === "cost_diff"
                ? "total-row"
                : ""
            }
            getCellClassName={(params) =>
              params.id === "total" || params.id === "cost_diff"
                ? "total-cell"
                : ""
            }
          />
        </Fetching>
      </Box>
    </Box>
  );
};

const complianceTableSx: SxProps = {
  ...defaultTableSx,
  [".MuiDataGrid-row.total-row"]: {
    borderTop: `1px solid ${theme.palette.grey[600]}`,
  },
  [".MuiDataGrid-cell.total-cell"]: {
    fontWeight: "fontWeightBold",
  },
};

const buildRowsAndTotals = (
  values: ComplianceValue[],
  crewIds: string[],
  t: TFunction,
  unit: UnitType
): {
  rows: DiffRow[];
  totalsRow: DiffRow;
  costDiffRow: DiffRow | null;
  maxAbsDiff: number;
} => {
  const { rows, totalDiffs, costDiffs, maxAbsDiff } = computeComplianceDiffs(
    values,
    crewIds,
    unit
  );

  return {
    rows,
    totalsRow: {
      id: "total",
      materialName: t("Total"),
      diffs: totalDiffs,
      outlierHeatIdsByCrew: {},
    },
    costDiffRow: costDiffs
      ? {
          id: "cost_diff",
          materialName: t("costPerTon"),
          diffs: costDiffs,
          outlierHeatIdsByCrew: {},
        }
      : null,
    maxAbsDiff,
  };
};

interface BuildColumnsArgs {
  crewIds: string[];
  t: TFunction;
  formatUnits: (value: number, unit: UnitType) => string;
  maxAbsDiff: number;
  formatSpecificCost: (value: number) => string;
  unit: UnitType;
  onAllOutlierClick?: (heatIds: string[], materialName: string) => void;
  materialDisplayName: (materialName: string) => string;
}

const buildColumns = ({
  crewIds,
  t,
  formatUnits,
  maxAbsDiff,
  formatSpecificCost,
  unit,
  onAllOutlierClick,
  materialDisplayName,
}: BuildColumnsArgs): GridColDef<DiffRow>[] => [
  {
    field: "materialName",
    headerName: t("material_name"),
    flex: 2,
    valueFormatter: (value: string) => materialDisplayName(value),
  },
  ...crewIds.map((crewId): GridColDef<DiffRow> => ({
    field: `crew_${crewId}`,
    headerName: crewId,
    flex: 1,
    align: "center",
    cellClassName: "coloured-cell",
    renderCell: (params) => {
      const diff = params.row.diffs[crewId] ?? 0;
      const isSummaryRow =
        params.row.id === "total" || params.row.id === "cost_diff";
      const isCostDiff = params.row.id === "cost_diff";
      const normalized = maxAbsDiff > 0 ? Math.abs(diff) / maxAbsDiff : 0;

      const content = isCostDiff
        ? formatSpecificCost(diff)
        : formatUnits(diff, unit);

      const outlierHeatIds =
        params.row.outlierHeatIdsByCrew[crewId] ?? EMPTY_HEAT_IDS;
      const isClickable =
        onAllOutlierClick !== undefined &&
        crewId === ALL_CREW_ID &&
        !isSummaryRow &&
        outlierHeatIds.length > 0;

      const cell = (
        <ColouredCell
          content={content}
          alpha={isSummaryRow ? 0 : Math.pow(normalized, 0.5)}
          color={theme.palette.data.blue}
          justifyContent="center"
        />
      );

      if (!isClickable) {
        return cell;
      }

      return (
        <Box
          onClick={() => {
            onAllOutlierClick(outlierHeatIds, params.row.materialName);
          }}
          sx={{
            width: "100%",
            height: "100%",
            cursor: "pointer",
            "&:hover": { fontWeight: "fontWeightSemiBold" },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          {cell}
          <NavigateNextIcon
            sx={{
              fontSize: 16,
              position: "absolute",
              right: 4,
              opacity: 0.5,
            }}
          />
        </Box>
      );
    },
  })),
];
